import { Network } from 'lucide-react'
import { DocSection } from '@/components/docs/doc-section'
import { DocPageHeader } from '@/components/docs/doc-page-header'
import { DocNav } from '@/components/docs/doc-nav'
import { MermaidDiagram } from '@/components/docs/mermaid-diagram'
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'

export function ArchitecturePage() {
  return (
    <>
      <DocPageHeader icon={Network} title="Architecture" />

      <div className="flex flex-1 flex-col gap-6 overflow-auto p-6 max-w-4xl">
        <DocSection title="ABAC Architecture Components">
          <p className="text-sm text-muted-foreground leading-relaxed">
            The XACML standard defines four components that work together to enforce access control.
            Each has a specific role in the authorization pipeline. Understanding them helps you see
            how poli-x protects your data end-to-end.
          </p>
        </DocSection>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Card className="border-t-4 border-teal-500">
            <CardHeader>
              <Badge className="bg-teal-100 text-teal-800 w-fit">PAP</Badge>
              <CardTitle className="text-sm">Policy Administration Point</CardTitle>
              <CardDescription className="text-xs leading-relaxed">
                Where administrators create, manage, and deploy access policies. In poli-x: the
                Policy Admin Console (PAC) and PAP API. This is where you define the rules that
                govern who can access what data and under which conditions.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-t-4 border-blue-500">
            <CardHeader>
              <Badge className="bg-blue-100 text-blue-800 w-fit">PDP</Badge>
              <CardTitle className="text-sm">Policy Decision Point</CardTitle>
              <CardDescription className="text-xs leading-relaxed">
                Evaluates authorization requests against policies and returns decisions. In poli-x: a
                high-performance Go engine that processes XACML requests in microseconds with
                built-in caching for production-scale throughput.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-t-4 border-amber-500">
            <CardHeader>
              <Badge className="bg-amber-100 text-amber-800 w-fit">PEP</Badge>
              <CardTitle className="text-sm">Policy Enforcement Point</CardTitle>
              <CardDescription className="text-xs leading-relaxed">
                Intercepts user requests and enforces PDP decisions. In poli-x: a database proxy that
                applies row-level filtering, column masking, and write protection at the wire
                protocol level — no application code changes needed.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-t-4 border-purple-500">
            <CardHeader>
              <Badge className="bg-purple-100 text-purple-800 w-fit">PIP</Badge>
              <CardTitle className="text-sm">Policy Information Point</CardTitle>
              <CardDescription className="text-xs leading-relaxed">
                Provides additional attributes the PDP needs for evaluation. In poli-x: the User
                Attribute Store (UAS) with IdP sync, so subject attributes like department, role,
                and clearance level are always up-to-date.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>

        <DocSection title="How They Work Together">
          <MermaidDiagram
            src="/docs/diagrams/architecture-components.svg"
            alt="ABAC architecture showing PAP, PDP, PEP, and PIP relationships"
            caption="ABAC component relationships"
          />
        </DocSection>

        <Separator />

        <DocSection title="End-to-End: SQL Query Authorization">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Here's how poli-x processes a real database query, from the moment a user sends SQL to the
            moment they receive results. Each component plays its part in a pipeline that takes
            milliseconds to execute.
          </p>
          <MermaidDiagram
            src="/docs/diagrams/architecture-sequence.svg"
            alt="End-to-end sequence diagram showing SQL query authorization flow"
            caption="End-to-end SQL query authorization through poli-x"
          />
        </DocSection>

        <DocNav prev={{ title: 'Request Evaluation', url: '/docs/evaluation' }} />
      </div>
    </>
  )
}
